import { useEffect, useState, type FormEvent } from 'react';
import type { PhoneStatus } from '@qm/contracts/customer-setup';
import { Button, Field, Heading, Text, TextInput, Select } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { bff, bffJson, BffError } from '../../../lib/bff';
import { setupCopy } from './copy';
import './setup.css';
const numeric = (v: string) =>
  v
    .replace(/[٠-٩۰-۹]/g, (c) => String(c.charCodeAt(0) - (c <= '٩' ? 0x660 : 0x6f0)))
    .replace(/\D/g, '');
export function PhoneVerification({
  onVerified,
  onLater,
}: {
  onVerified: () => void;
  onLater?: () => void;
}) {
  const { locale } = useApp(),
    t = setupCopy(locale);
  const [state, setState] = useState<PhoneStatus | null>(null),
    [number, setNumber] = useState(''),
    [prefix, setPrefix] = useState('+971'),
    [code, setCode] = useState(''),
    [consent, setConsent] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [now, setNow] = useState(Date.now());
  useEffect(() => {
    let live = true;
    void bff<PhoneStatus>('/v1/identity/phone/status').then(
      (s) => {
        if (live) setState(s);
      },
      () => {
        if (live) setError(t.error);
      },
    );
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, []);
  const run = async (fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (e) {
      setError(e instanceof BffError && e.status === 429 ? t.cooldown : t.deliveryError);
    } finally {
      setBusy(false);
    }
  };
  const send = () =>
    run(async () => {
      const phone = number.trim().startsWith('+')
        ? '+' + numeric(number)
        : prefix + numeric(number).replace(/^0/, '');
      const res = await bffJson<{
        challengeId: string;
        maskedPhone: string;
        expiresAt: number;
        resendAt: number;
      }>('/v1/identity/phone/challenge', 'POST', {
        ...(number ? { phone } : { challengeId: state?.challengeId }),
        locale,
        consent: state?.challengeId ? true : consent,
      });
      setState((s) => ({ ...s!, ...res, verified: false }));
      setCode('');
    });
  const verify = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      try {
        const s = await bffJson<PhoneStatus>('/v1/identity/phone/verify', 'POST', {
          challengeId: state?.challengeId,
          code,
        });
        setState(s);
        setCode('');
        if (s.verified) onVerified();
      } catch {
        setError(t.wrongCode);
      }
    });
  };
  const change = () =>
    run(async () => {
      setState(await bffJson<PhoneStatus>('/v1/identity/phone/cancel', 'POST', {}));
      setCode('');
      setNumber('');
      setConsent(false);
    });
  const assistance = () =>
    run(async () =>
      setState(await bffJson<PhoneStatus>('/v1/identity/phone/assistance', 'POST', {})),
    );
  const wait = Math.max(0, Math.ceil(((state?.resendAt ?? 0) - now) / 1000)),
    left = Math.max(0, Math.ceil(((state?.expiresAt ?? 0) - now) / 1000));
  return (
    <section className="qm-setup__phone" aria-labelledby="phone-title">
      <Heading level={2} id="phone-title">
        {t.phoneTitle}
      </Heading>
      <Text>{t.phoneIntro}</Text>
      {!state && !error ? (
        <Text role="status">{t.loading}</Text>
      ) : state?.verified ? (
        <>
          <Text role="status">
            {t.verified} <bdi>{state.maskedPhone}</bdi>
          </Text>
          <Button type="button" onClick={onVerified}>
            {t.continue}
          </Button>
        </>
      ) : state?.available ? (
        <>
          {!state.challengeId ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void send();
              }}
            >
              <div className="qm-setup__phone-row">
                <Field label={t.country}>
                  {({ id }) => (
                    <Select id={id} value={prefix} onChange={(e) => setPrefix(e.target.value)}>
                      <option value="+971">AE +971</option>
                      <option value="+966">SA +966</option>
                      <option value="+974">QA +974</option>
                      <option value="+968">OM +968</option>
                      <option value="+973">BH +973</option>
                      <option value="+965">KW +965</option>
                      <option value="+1">US/CA +1</option>
                      <option value="+44">UK +44</option>
                      <option value="+91">IN +91</option>
                      <option value="">Other (+)</option>
                    </Select>
                  )}
                </Field>
                <Field label={t.phone}>
                  {({ id }) => (
                    <TextInput
                      id={id}
                      type="tel"
                      autoComplete="tel-national"
                      inputMode="tel"
                      dir="ltr"
                      required
                      maxLength={32}
                      value={number}
                      onChange={(e) => setNumber(e.target.value)}
                    />
                  )}
                </Field>
              </div>
              <label className="qm-setup__check">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                />
                {t.consent}
              </label>
              <Button
                type="submit"
                variant="primary"
                disabled={busy || !consent || wait > 0 || number.length < 5}
              >
                {t.sendCode}
              </Button>
              {wait > 0 ? (
                <Text role="status">
                  {wait} {t.seconds}
                </Text>
              ) : null}
            </form>
          ) : (
            <>
              <Text>
                {t.sentTo} <bdi>{state.maskedPhone}</bdi>
              </Text>
              <form onSubmit={verify}>
                <Field label={t.code}>
                  {({ id }) => (
                    <TextInput
                      id={id}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      value={code}
                      onChange={(e) => setCode(numeric(e.target.value))}
                      required
                      dir="ltr"
                    />
                  )}
                </Field>
                <Text role="status">
                  {t.expires} {left} {t.seconds}
                </Text>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={busy || code.length !== 6 || left === 0}
                >
                  {t.verify}
                </Button>
              </form>
              <div className="qm-setup__actions">
                <Button type="button" disabled={busy || wait > 0} onClick={() => void send()}>
                  {t.resend}
                  {wait > 0 ? ` (${wait})` : ''}
                </Button>
                <Button type="button" disabled={busy} onClick={() => void change()}>
                  {t.changeNumber}
                </Button>
              </div>
            </>
          )}
        </>
      ) : (
        <Text role="status">{t.unavailable}</Text>
      )}
      {error ? (
        <p role="alert" className="qm-field__error">
          {error}
        </p>
      ) : null}
      {state && !state.verified ? (
        <>
          <Button type="button" disabled={busy} onClick={() => void assistance()}>
            {t.assistance}
          </Button>
          {state.assistanceRequested ? <Text role="status">{t.assistanceSent}</Text> : null}
          {!state.required && onLater ? (
            <Button type="button" onClick={onLater}>
              {t.later}
            </Button>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
